/**
 * SuggestedSearchSortableItemV2 — single row in the Suggested Searches
 * editor. Three selects (object type → object name/title → search
 * behavior) inside a cream card with drag handle on the left and a
 * trash icon on the right.
 *
 * Object types map to different "name" lookups via the `data_options`
 * payload from `fetchAvailableOption`. The shape mirrors v1's
 * SuggestedSearchSortableItem.js.
 */
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton, cn } from '../../redesign';
import { Select } from '../../redesign/edit';

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 13, height: 13 }}>
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const OBJECT_TYPES = [
  'AUTHOR',
  'SESSION',
  'SERIES',
  'AUDIOBOOK',
  'CHALLENGE',
  'QUICK_TAP_SESSION',
  'CARD_DECK',
];

const SEARCH_BEHAVIORS = ['REDIRECT', 'SEARCH'];

/* Pretty-print the SCREAMING_SNAKE labels — "QUICK_TAP_SESSION" reads
 * better as "Quick Tap Session" in the dropdown. */
function prettyLabel(raw) {
  return raw
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/* Pull the right list out of the data_options bundle and shape it
 * into [{ value, label }]. Returns [] for unknown / empty types so
 * the consumer can render a disabled select. */
function lookupOptions(data_options, object_type) {
  if (!data_options || !object_type) return [];
  switch (object_type) {
    case 'AUTHOR':
      return (data_options.authors || []).map((el) => ({
        value: el.author_id,
        label: el.author_name,
      }));
    case 'SESSION':
      return (data_options.sessions || []).map((el) => ({
        value: el.session_id,
        label: el.session_name,
      }));
    case 'SERIES':
      return (data_options.series || []).map((el) => ({
        value: el.series_id,
        label: el.series_title,
      }));
    case 'AUDIOBOOK':
      return (data_options.audiobook || []).map((el) => ({
        value: el.subcategory_id,
        label: el.subcategory_title,
      }));
    case 'CHALLENGE':
      return (data_options.challenges || []).map((el) => ({
        value: el.challenge_id,
        label: el.challenge_title,
      }));
    case 'QUICK_TAP_SESSION':
      return (data_options.quick_tap_sessions || []).map((el) => ({
        value: el.quick_tap_session_id,
        label: el.quick_tap_title,
      }));
    case 'CARD_DECK':
      return (data_options.card_decks || []).map((el) => ({
        value: el.deck_id,
        label: el.deck_name,
      }));
    default:
      return [];
  }
}

export default function SuggestedSearchSortableItemV2({
  data,
  data_options,
  el_index,
  handleChange,
  handleDelete,
  id,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const nameOptions = lookupOptions(data_options, data.object_type);
  const currentValueExists = nameOptions.some(
    (o) => String(o.value) === String(data.object_id),
  );
  const nameValue = currentValueExists ? data.object_id : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-surface border border-sand"
    >
      <div
        className="grid items-center"
        style={{
          gridTemplateColumns: '36px minmax(0, 1.8fr) minmax(0, 2.6fr) minmax(0, 1.4fr) 36px',
          gap: 10,
          padding: '10px 8px',
        }}
      >
        {/* Drag handle */}
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'grid place-items-center text-text-muted shrink-0 cursor-grab',
            'hover:text-navy active:cursor-grabbing',
            'focus:outline-none focus-visible:text-navy',
          )}
          style={{ width: 36, height: 36, touchAction: 'none' }}
        >
          {DragHandleIcon}
        </span>

        {/* Object Type */}
        <Select
          value={data.object_type || ''}
          onChange={(e) =>
            handleChange({ value: e.target.value, prop: 'object_type' }, el_index)
          }
          aria-label="Object type"
        >
          <option value="" disabled>
            Object type…
          </option>
          {OBJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {prettyLabel(t)}
            </option>
          ))}
        </Select>

        {/* Object Name / Title — disabled if no type yet, or if the
         *  lookup bundle hasn't arrived. */}
        {nameOptions.length > 0 ? (
          <Select
            value={nameValue}
            onChange={(e) =>
              handleChange({ value: e.target.value, prop: 'object_id' }, el_index)
            }
            aria-label="Object name / title"
          >
            <option value="" disabled>
              Select…
            </option>
            {nameOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        ) : (
          <Select value="" disabled aria-label="Object name / title">
            <option value="">
              {data.object_type ? 'Loading options…' : 'Pick a type first'}
            </option>
          </Select>
        )}

        {/* Search behavior */}
        <Select
          value={data.search_behavior || ''}
          onChange={(e) =>
            handleChange({ value: e.target.value, prop: 'search_behavior' }, el_index)
          }
          aria-label="Search behavior"
        >
          <option value="" disabled>
            Behavior…
          </option>
          {SEARCH_BEHAVIORS.map((b) => (
            <option key={b} value={b}>
              {prettyLabel(b)}
            </option>
          ))}
        </Select>

        {/* Delete */}
        <IconButton
          size="sm"
          aria-label="Remove"
          title="Remove"
          onClick={() => handleDelete(data.id, el_index)}
          type="button"
        >
          {TrashIconSvg}
        </IconButton>
      </div>
    </div>
  );
}
